import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { RealtimeV2SocketService } from './realtime-v2-socket.service';
import {
  REALTIME_V2_SCOPE_KEYS,
  RealtimeV2AdmissionEntry,
  RealtimeV2AdmissionEvent,
} from './realtime-v2.models';

/**
 * Stato applicativo della coda di ammissione realtime v2. Vive SOPRA il socket, che si limita a
 * emettere eventi grezzi (`admissionEvents$`): qui li riduciamo in una mappa indicizzata per
 * `scopeKey`, così più scope (home, progetto, issue, linked-preview) possono essere in attesa
 * contemporaneamente senza sovrascriversi. La UI consuma i selettori di questo service, non il socket.
 *
 * Regola chiave: un'attesa esiste solo mentre lo scope è in coda sul server (`queued`). Appena lo
 * slot viene concesso (`accepted`) l'attesa si chiude: la pagina di coda scompare e il sync prosegue
 * col contenuto normale (l'overlay di first-sync copre quella fase). Un ingresso ammesso subito
 * (nessuna coda) non genera mai stato bloccante. `ready`/`settled` sono chiusure difensive.
 *
 * Seconda regola: si riduce solo ciò che ha senso MOSTRARE (vedi USER_FACING_SCOPES). Anche issue e
 * linked preview aspettano il proprio turno sul server esattamente come home e progetto, ma in
 * silenzio: sono contenuti secondari dentro una vista già montata e disegnare lì una coda che scorre
 * sarebbe rumore, non informazione.
 */
@Injectable({
  providedIn: 'root',
})
export class RealtimeV2AdmissionService {
  private readonly _admissions$ = new BehaviorSubject<ReadonlyMap<string, RealtimeV2AdmissionEntry>>(new Map());

  /** Mappa scopeKey → stato di attesa corrente. */
  public readonly admissions$: Observable<ReadonlyMap<string, RealtimeV2AdmissionEntry>> = this._admissions$.asObservable();

  constructor(private socket: RealtimeV2SocketService) {
    this.socket.admissionEvents$.subscribe((event) => this.reduce(event));

    // Su disconnessione il server perde la coda: azzeriamo lo stato locale. Alla riconnessione il
    // runtime rifà gli enter e gli eventi ricostruiranno le attese.
    this.socket.connected$
      .pipe(distinctUntilChanged())
      .subscribe((connected) => {
        if (!connected) {
          this.clear();
        }
      });
  }

  /** Attesa corrente per uno scope specifico (o null se non in attesa). */
  public admissionFor(scopeKey: string): Observable<RealtimeV2AdmissionEntry | null> {
    return this._admissions$.pipe(
      map((admissions) => admissions.get(scopeKey) ?? null),
      distinctUntilChanged(),
    );
  }

  /** Attesa dello scope globale (enterHome). */
  public readonly homeAdmission$: Observable<RealtimeV2AdmissionEntry | null> = this.admissionFor(
    REALTIME_V2_SCOPE_KEYS.GLOBAL,
  );

  /** Attesa dello scope di un progetto. */
  public projectAdmission$(projectId: string): Observable<RealtimeV2AdmissionEntry | null> {
    return this.admissionFor(`${REALTIME_V2_SCOPE_KEYS.PROJECT_PREFIX}${projectId}`);
  }

  /**
   * Scope la cui attesa in coda è esposta alla UI: sono gli ingressi che occupano l'intera schermata
   * e per cui l'utente resta davanti a una pagina vuota (home e progetto). Per tutti gli altri
   * l'attesa esiste ma non viene pubblicata.
   */
  private isUserFacing(scopeKey: string): boolean {
    return scopeKey === REALTIME_V2_SCOPE_KEYS.GLOBAL
      || scopeKey.startsWith(REALTIME_V2_SCOPE_KEYS.PROJECT_PREFIX);
  }

  private reduce(event: RealtimeV2AdmissionEvent): void {
    if (!this.isUserFacing(event.scopeKey)) {
      return;
    }

    switch (event.kind) {
      case 'queued': {
        this.set(event.scopeKey, {
          scopeKey: event.scopeKey,
          requestId: event.requestId,
          command: event.command,
          position: event.position,
          queued: event.queued,
          active: event.active,
          concurrency: event.concurrency,
        });
        break;
      }
      case 'accepted':
      case 'ready':
      case 'settled':
        // L'attesa rappresenta solo la permanenza in coda: appena lo slot è concesso (`accepted`)
        // la chiudiamo, così la pagina di coda scompare e il sync prosegue col contenuto normale.
        // `ready`/`settled` restano come chiusura difensiva per i flussi senza coda.
        this.delete(event.scopeKey);
        break;
      default:
        break;
    }
  }

  private set(scopeKey: string, entry: RealtimeV2AdmissionEntry): void {
    const next = new Map(this._admissions$.value);
    next.set(scopeKey, entry);
    this._admissions$.next(next);
  }

  private delete(scopeKey: string): void {
    if (!this._admissions$.value.has(scopeKey)) {
      return;
    }
    const next = new Map(this._admissions$.value);
    next.delete(scopeKey);
    this._admissions$.next(next);
  }

  private clear(): void {
    if (this._admissions$.value.size === 0) {
      return;
    }
    this._admissions$.next(new Map());
  }
}
